import React, { useEffect } from 'react';
import { Box, Group } from '@mantine/core';
import { useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useInView } from 'react-intersection-observer';
import Lottie from 'react-lottie-player';
// eslint-disable-next-line import/order
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';

import { useImmerReducer } from 'use-immer';
import { useNavigate } from 'react-router-dom';

import { useIsMobileMedia, useNextPath, useUser } from '~/hooks';

import credLogo from '~/assets/icons/home/company-logos/cred.png';
import razorpayLogo from '~/assets/icons/home/company-logos/razorpay.png';
import smallcaseLogo from '~/assets/icons/home/company-logos/smallcase.png';
import swiggyLogo from '~/assets/icons/home/company-logos/swiggy.png';
import urbancompanyLogo from '~/assets/icons/home/company-logos/urbancompany.png';
import zetaLogo from '~/assets/icons/home/company-logos/zeta.png';
import Stars from '~/assets/icons/home/stars.svg';
import RightArrow from '~/assets/icons/right-arrow.svg';
import avatarsImg from '~/assets/images/home/illustrations/avatars.png';
import candidatesCardImg from '~/assets/images/home/illustrations/candidates-card.png';
import hiringCardImg from '~/assets/images/home/illustrations/hiring-card.png';
import shortlistCardImg from '~/assets/images/home/illustrations/shortlist-card.png';
import cherryPickBottomJson from '~/assets/images/home/lottie/cherry-pick-bottom.json';
import cherryPickJson from '~/assets/images/home/lottie/cherry-pick.json';
import heroJson from '~/assets/images/home/lottie/hero.json';
import inviteOnlyJson from '~/assets/images/home/lottie/invite-only.json';
import proofOfWorkJson from '~/assets/images/home/lottie/proof-of-work.json';
import rigorousUpskillingJson from '~/assets/images/home/lottie/rigorous-upskilling.json';
import robustScreeningJson from '~/assets/images/home/lottie/robust-screening.json';

import AuthModal from './components/AuthModal';
import FAQ from './components/FAQ';
import HowCard from './components/HowCard';
import UserTestimonial from './components/UserTestimonial';
import ValuePointsList from './components/ValuePointsList';
import { hireFaqsList, hireValuePoints, userTestimonials } from './constants';
import AuthContext, { authContextInitialState, authContextSlice } from './contexts/AuthContext';
import Footer from './Footer';
import Header from './Header';
import * as Styles from './index.styled';
import {
  LabelLargeStrong,
  ParagraphLarge,
  ParagraphLargeStrong,
  ParagraphMedium,
  TitleLarge,
  TitleXLarge,
  TitleXXLarge,
  TitleXXSmall,
} from './typography';

const userTestimonialSliderSettings = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  // appendDots: (dots) => (
  //   <Styles.SliderDotsContainer>
  //     <ul>{dots}</ul>
  //   </Styles.SliderDotsContainer>
  // ),
};

const HireLandingPage = ({ showAuthView }) => {
  const isMobileMedia = useIsMobileMedia();
  const user = useUser();
  const nextPath = useNextPath('/candidates/browse/');
  const navigate = useNavigate();

  const heroSectionRef = useRef(null);
  const userTestimonialSliderRef = useRef(null);

  const [authCtx, dispatchAuthCtx] = useImmerReducer(authContextSlice.reducer, authContextInitialState);

  const { ref: cherryPickLottieRef, inView: cherryPickLottieInView } = useInView({
    threshold: 0.6,
  });

  const { ref: invitesLottieRef, inView: invitesLottieInView } = useInView({
    threshold: 0.8,
  });

  const { ref: rigorousUpskillingLottieRef, inView: rigorousUpskillingLottieInView } = useInView({
    threshold: 0.8,
  });

  const { ref: robustScreeningLottieRef, inView: robustScreeningLottieInView } = useInView({
    threshold: 0.8,
  });

  const { ref: proofOfWorkLottieRef, inView: proofOfWorkLottieInView } = useInView({
    threshold: 0.8,
  });

  const pageTitle = 'Hire Product Managers trained by Upraised';

  const setAuthModalView = () => {
    if (user) {
      if (user.isEmailVerified) {
        navigate(nextPath);
      } else {
        dispatchAuthCtx(authContextSlice.actions.setView('verify-email'));
      }
    }
  };

  const openAuthModal = () => {
    dispatchAuthCtx(authContextSlice.actions.setOpen(true));
  };

  const closeAuthModal = () => {
    dispatchAuthCtx(authContextSlice.actions.setOpen(false));
  };

  const handleBrowseCandidatesClick = () => {
    navigate('/auth');
  };

  const handleAuthModalClose = () => {
    dispatchAuthCtx(authContextSlice.actions.setView('login'));
    navigate('/');
  };

  useEffect(() => {
    if (showAuthView) {
      setAuthModalView();
      openAuthModal();
    } else {
      closeAuthModal();
    }
  }, [showAuthView]);

  useEffect(() => {
    setAuthModalView();
  }, [user]);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Styles.Root>
        <Header />
        <Styles.Section mt={isMobileMedia ? undefined : '-90px'} ref={heroSectionRef}>
          <Styles.Container>
            <Styles.HeroFlexContainer>
              <Styles.HeroTextWrapper>
                <TitleXXLarge>
                  Hire brilliant product managers effortlessly
                  <span style={{ color: '#0C7BEB', marginRight: '8px' }}>.</span>
                  <Stars width={52} height={52} />
                </TitleXXLarge>
                <ParagraphLarge mt="12px">
                  Pick from exemplary product managers with verified skills, in record-time & build your A-team for
                  free.
                </ParagraphLarge>
                <Styles.HeroBtnWrapper>
                  <Styles.PrimaryButton onClick={handleBrowseCandidatesClick}>
                    Browse Candidates
                    <RightArrow width={24} height={24} />
                  </Styles.PrimaryButton>
                  <Styles.OutlinedBtn
                    as="a"
                    href={'https://calendly.com/hire-from-upraised/15-min'}
                    target="_blank"
                    style={{
                      textDecoration: 'none',
                      padding: '16px',
                      minWidth: isMobileMedia ? undefined : '160px',
                      width: isMobileMedia ? undefined : '160px',
                    }}
                  >
                    Book a Call
                  </Styles.OutlinedBtn>
                </Styles.HeroBtnWrapper>
              </Styles.HeroTextWrapper>
              <Styles.HeroLottieWrapper>
                <Styles.ScaleHeroLottieContainer>
                  <Lottie loop animationData={heroJson} play />
                </Styles.ScaleHeroLottieContainer>
              </Styles.HeroLottieWrapper>
            </Styles.HeroFlexContainer>
          </Styles.Container>
        </Styles.Section>

        <Styles.CompaniesSection>
          <Styles.Container>
            <Group>
              <TitleXXSmall color="#9F9F9F">THE BEST PRODUCT TEAMS HIRE WITH UPRAISED</TitleXXSmall>
              <Styles.TitleBorder />
            </Group>
            <Styles.CompaniesLogoList>
              <Styles.CompaniesLogoItem>
                <img src={credLogo} alt="Cred" />
              </Styles.CompaniesLogoItem>
              <Styles.CompaniesLogoItem>
                <img src={razorpayLogo} alt="Razorpay" />
              </Styles.CompaniesLogoItem>
              <Styles.CompaniesLogoItem>
                <img src={swiggyLogo} alt="Swiggy" />
              </Styles.CompaniesLogoItem>
              <Styles.CompaniesLogoItem>
                <img src={urbancompanyLogo} alt="Urban Company" />
              </Styles.CompaniesLogoItem>
              <Styles.CompaniesLogoItem>
                <img src={smallcaseLogo} alt="Smallcase" />
              </Styles.CompaniesLogoItem>
              <Styles.CompaniesLogoItem>
                <img src={zetaLogo} alt="Zeta" />
              </Styles.CompaniesLogoItem>
            </Styles.CompaniesLogoList>
          </Styles.Container>
        </Styles.CompaniesSection>

        <Styles.CherryPickSection>
          <Styles.Container py="120px">
            <Styles.TextWrapperLg>
              <TitleXLarge>
                We <span style={{ color: '#0C7BEB' }}>cherry pick</span> talent for you, so you can spend time building
                great products
              </TitleXLarge>
              <ParagraphLarge style={{ marginTop: '12px' }}>
                It takes ~8 hours per candidate to verify the skills on their resume. Even more to verify their
                soft-skills. Our robust screening & upskilling helps you save ~5 hours and close the loop in
                record-time.
              </ParagraphLarge>
            </Styles.TextWrapperLg>
            <Styles.CherryPickLottie ref={cherryPickLottieRef}>
              <Lottie
                animationData={isMobileMedia ? cherryPickJson : cherryPickBottomJson}
                loop={false}
                goTo={0}
                play={cherryPickLottieInView}
              />
            </Styles.CherryPickLottie>
          </Styles.Container>
        </Styles.CherryPickSection>

        <Styles.QualityPointsSection>
          <Styles.Container>
            <Styles.QualityPointsContainer>
              <Styles.TextWrapper>
                <TitleXXSmall style={{ color: '#0C7BEB' }}>INVITE ONLY</TitleXXSmall>
                <TitleLarge my={isMobileMedia ? '8px' : '12px'}>
                  Natural problems solvers are invited and made 10x better
                </TitleLarge>
                <ParagraphMedium color="#434343">
                  Not everyone can be an Upraiser. Every Upraiser has benchmarked their core product skills via our
                  trademark Know-YourSelf (KYS) Test. Top PMs at Amazon,Google & Airbnb trust the KYS test to evaluate
                  the following skills:
                </ParagraphMedium>
                <ValuePointsList points={hireValuePoints} />
                <Styles.PointsLinkContainer>
                  <Styles.NavLink
                    href="https://www.upraised.co/kys-know-yourself-test"
                    title="KYS Test"
                    target="_blank"
                  >
                    Learn More about the KYS test
                  </Styles.NavLink>
                  <RightArrow width={24} height={24} />
                </Styles.PointsLinkContainer>
              </Styles.TextWrapper>
              <Styles.PointsLottieLeft ref={invitesLottieRef}>
                <Lottie animationData={inviteOnlyJson} loop={false} goTo={0} play={invitesLottieInView} />
              </Styles.PointsLottieLeft>
            </Styles.QualityPointsContainer>
          </Styles.Container>
        </Styles.QualityPointsSection>

        <Styles.QualityPointsSection>
          <Styles.Container>
            <Styles.QualityPointsContainer>
              <Styles.PointsLottieRight ref={rigorousUpskillingLottieRef}>
                <Lottie
                  animationData={rigorousUpskillingJson}
                  loop={false}
                  goTo={0}
                  play={rigorousUpskillingLottieInView}
                />
              </Styles.PointsLottieRight>
              <Styles.TextWrapper>
                <TitleXXSmall style={{ color: '#0C7BEB' }}>RIGOROUS UPSKILLING</TitleXXSmall>
                <TitleLarge my={isMobileMedia ? '8px' : '12px'}>
                  Learning happens with experts & not by trial and error
                </TitleLarge>
                <ParagraphMedium color="#434343">
                  Top product leaders & managers from companies like Microsoft, Uber, Amazon and more help our
                  candidates hone their strengths & bridge their gaps. Every candidate in the Career Accelerator
                  Program:
                </ParagraphMedium>
                <ValuePointsList
                  points={[
                    'Is refined via feedback based on 20+ hours of 1:1 expert sessions',
                    'Has mastery in core & advanced concepts vital to ship killer products',
                    'Is trained via hands-on activities in live workshops to get industry-experience',
                    'Is proficient with tools like Figma, Miro, Google Optimise, Mixpanel and Retool',
                  ]}
                />
                <Styles.PointsLinkContainer>
                  <Styles.NavLink
                    href="https://www.upraised.co/career-accelerator-program/how-cap-works"
                    title="How CAP Works"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {isMobileMedia ? 'Career Accelerator Program' : 'Find how our Career Accelerator Program works'}
                  </Styles.NavLink>
                  <RightArrow width={24} height={24} />
                </Styles.PointsLinkContainer>
              </Styles.TextWrapper>
            </Styles.QualityPointsContainer>
          </Styles.Container>
        </Styles.QualityPointsSection>

        <Styles.QualityPointsSection>
          <Styles.Container>
            <Styles.QualityPointsContainer>
              <Styles.TextWrapper>
                <TitleXXSmall style={{ color: '#0C7BEB' }}>ROBUST SCREENING</TitleXXSmall>
                <TitleLarge my={isMobileMedia ? '8px' : '12px'}>
                  Only candidates with grit & growth mindset make it to you
                </TitleLarge>
                <ParagraphMedium color="#434343">
                  Every candidate has commited to improving themselves by spending 20 hours/week for 16 weeks to perform
                  consistenty well in all our assessments. Furthermore our experts have screened the skills of a
                  candidate to:
                </ParagraphMedium>
                <ValuePointsList
                  points={[
                    'Clarify goals and objectives',
                    'Empathise with users & their pain points',
                    'Prioritise problems & solutions to focus on',
                    'Think of creative and innovative ideas',
                    'Work well with engineering & design teams',
                    'Generate actionable insights via data analysis',
                  ]}
                />
                <Styles.PointsLinkContainer>
                  <Styles.NavLink
                    href="https://www.upraised.co/coaches"
                    title="View All Experts"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    See experts who screen our candidates
                  </Styles.NavLink>
                  <RightArrow width={24} height={24} />
                </Styles.PointsLinkContainer>
              </Styles.TextWrapper>
              <Styles.PointsLottieLeft ref={robustScreeningLottieRef}>
                <Lottie animationData={robustScreeningJson} loop={false} goTo={0} play={robustScreeningLottieInView} />
              </Styles.PointsLottieLeft>
            </Styles.QualityPointsContainer>
          </Styles.Container>
        </Styles.QualityPointsSection>

        <Styles.QualityPointsSection>
          <Styles.Container>
            <Styles.QualityPointsContainer>
              <Styles.PointsLottieRight ref={proofOfWorkLottieRef}>
                <Lottie animationData={proofOfWorkJson} loop={false} goTo={0} play={proofOfWorkLottieInView} />
              </Styles.PointsLottieRight>
              <Styles.TextWrapper>
                <TitleXXSmall style={{ color: '#0C7BEB' }}>PROOF OF WORK</TitleXXSmall>
                <TitleLarge my={isMobileMedia ? '8px' : '12px'}>No gaps in what you see & what you get</TitleLarge>
                <ParagraphMedium color="#434343">
                  Proof of work acts as the best signal to be sure of a candidate’s abilities. Before interviewing, see
                  their work so that you have the correct expectations.
                </ParagraphMedium>
                <ValuePointsList
                  points={[
                    'Check their rigour, problem identification & solution quality with PRDs',
                    'Check how they approached & solved case studies shared by Upraised',
                    'Check their product sense basis observations made on products they use',
                    'Check their executional skills via wireframes, technical specs and more',
                  ]}
                />
                <Styles.PointsLinkContainer>
                  <Styles.NavLink
                    href="https://www.upraised.co/portfolio/shubham"
                    title="Portfolio: Shubham"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    See sample product work
                  </Styles.NavLink>
                  <RightArrow width={24} height={24} />
                </Styles.PointsLinkContainer>
              </Styles.TextWrapper>
            </Styles.QualityPointsContainer>
          </Styles.Container>
        </Styles.QualityPointsSection>

        <Styles.TestimonialsSection>
          <Styles.Container>
            <TitleXLarge>Hear from our partners</TitleXLarge>
            <Styles.TestimonialSliderContainer>
              <Slider {...userTestimonialSliderSettings} ref={userTestimonialSliderRef}>
                {userTestimonials.map((testimonial) => (
                  <UserTestimonial testimonial={testimonial} key={`${testimonial.name}-${testimonial.designation}`} />
                ))}
              </Slider>
              <Styles.SliderArrowBtnLeft onClick={() => userTestimonialSliderRef.current.slickPrev()}>
                <Styles.ChevronRightIcon />
              </Styles.SliderArrowBtnLeft>
              <Styles.SliderArrowBtnRight onClick={() => userTestimonialSliderRef.current.slickNext()}>
                <Styles.ChevronRightIcon />
              </Styles.SliderArrowBtnRight>
            </Styles.TestimonialSliderContainer>
          </Styles.Container>
        </Styles.TestimonialsSection>

        <Styles.HowSection>
          <Styles.Container>
            <Styles.TextWrapperLg>
              <TitleXLarge my={isMobileMedia ? '8px' : '12px'}>
                How do we make hiring efficient & effective for you?
              </TitleXLarge>
              <ParagraphLarge>
                Hiring with Upraised is all about saving resources and getting the best quality candidates. You only
                browse through the already vetted candidates. If you feel stuck, reach out to us and we will help you
                find the right fit even quicker.
              </ParagraphLarge>
            </Styles.TextWrapperLg>
            <Styles.HowCardsContainer>
              <Styles.HowCardsItem>
                <HowCard
                  tag="01"
                  title="Browse candidate profiles"
                  text="Scan among the screened pool of Upraisers.
                        Feel free to check their resume, case studies, PRDs with other product work included in their portfolio."
                  img={candidatesCardImg}
                />
              </Styles.HowCardsItem>
              <Styles.HowCardsItem>
                <HowCard
                  tag="02"
                  title="Shortlist matching ones"
                  text="Shortlist the profiles that fit your requirement.
                        If you get stuck, Upraised will help you in finding the perfect match even quicker and will keep
                        on sending more profiles as they come."
                  img={shortlistCardImg}
                />
              </Styles.HowCardsItem>
              <Styles.HowCardsItem>
                <HowCard
                  tag="03"
                  title="Interview & hire"
                  text="Contact candidates directly to set up interviews and further assess their skills
                        according to your requirement. If they get through your internal hiring process,
                        roll out an offer to your new PM."
                  img={hiringCardImg}
                />
              </Styles.HowCardsItem>
            </Styles.HowCardsContainer>
          </Styles.Container>
        </Styles.HowSection>

        <Styles.BannerSection>
          <Styles.Container>
            <Styles.BannerContainer>
              <Styles.BannerImgContainer>
                <img src={avatarsImg} alt="" />
              </Styles.BannerImgContainer>
              <Styles.BannerContent mt="104px">
                <TitleXLarge color="#fff" style={{ textAlign: isMobileMedia ? 'left' : 'center' }}>
                  Scale-up your product team
                  {isMobileMedia ? null : <br />}
                  at <span style={{ color: '#1D99FF' }}>zero cost</span> today
                </TitleXLarge>
                <Box mt="46px" style={{ width: isMobileMedia ? '100%' : 'auto' }}>
                  <Styles.SecondaryBtn
                    rightIcon={<RightArrow width={24} color="#222" />}
                    onClick={handleBrowseCandidatesClick}
                    style={{ width: '100%' }}
                  >
                    <LabelLargeStrong color="#222">Browse Candidates</LabelLargeStrong>
                  </Styles.SecondaryBtn>
                </Box>
              </Styles.BannerContent>
            </Styles.BannerContainer>
          </Styles.Container>
        </Styles.BannerSection>

        <Styles.FaqSection>
          <Styles.Container>
            <Styles.FaqContainer>
              <Styles.FaqTextWrapper>
                <TitleXLarge>
                  Frequently <br /> Asked Questions
                </TitleXLarge>
                <ParagraphLarge>
                  Everything you need to know about hiring with Upraised. In case you have more questions, you can{' '}
                  <Styles.NavLink
                    style={{ display: 'inline-block' }}
                    href="mailto:hire@upraised.co"
                    title="Chat with us"
                  >
                    <ParagraphLargeStrong color="inherit" component={'span'}>
                      chat with us.
                    </ParagraphLargeStrong>
                  </Styles.NavLink>
                </ParagraphLarge>
              </Styles.FaqTextWrapper>
              <Styles.FaqWrapper>
                <FAQ items={hireFaqsList} />
              </Styles.FaqWrapper>
            </Styles.FaqContainer>
          </Styles.Container>
        </Styles.FaqSection>
        <Footer />
      </Styles.Root>

      <AuthContext.Provider value={{ authCtx, dispatchAuthCtx }}>
        <AuthModal onClose={handleAuthModalClose} />
      </AuthContext.Provider>
    </>
  );
};

export default HireLandingPage;
